import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../Button';
import './Header.scss';

/** Render a label string, converting `<br/>` to actual line breaks */
const renderLabel = (text) => {
  if (!text || !text.includes('<br/>')) return text;
  return text.split('<br/>').map((part, i, arr) => (
    <React.Fragment key={i}>
      {part}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));
};

export const Header = ({
  logoSrc = '/images/more-to-parkinsons-logo.png',
  logoAlt = 'More to Parkinson’s logo',
  logoHref = '/',
  utilityLinks = [],
  ctaLink = null,
  mainNavLinks = [],
  activeLink = '',
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedDropdown, setExpandedDropdown] = useState(null);
  const [desktopDropdown, setDesktopDropdown] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef(null);
  const lastScrollY = useRef(0);

  const toggleDropdown = (label) => {
    setExpandedDropdown((prev) => (prev === label ? null : label));
  };

  const toggleDesktopDropdown = (label) => {
    setDesktopDropdown((prev) => (prev === label ? null : label));
  };

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (!headerRef.current) return;
      setHeaderHeight(headerRef.current.offsetHeight || 0);
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    if (!headerRef.current) return;
    setHeaderHeight(headerRef.current.offsetHeight || 0);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousOverscrollBehavior = document.body.style.overscrollBehavior;

    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscrollBehavior;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    let scrollAccum = 0;
    const SHOW_THRESHOLD = 40;
    const HIDE_THRESHOLD = 10;
    let prevDirection = null;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;
      lastScrollY.current = currentY;
      const headerHeight = headerRef.current?.offsetHeight ?? 0;

      if (currentY <= headerHeight) {
        setHeaderVisible(true);
        setIsSticky(false);
        scrollAccum = 0;
        prevDirection = null;
        return;
      }

      const direction = delta > 0 ? 'down' : 'up';

      if (direction !== prevDirection) {
        scrollAccum = 0;
        prevDirection = direction;
      }

      scrollAccum += Math.abs(delta);

      if (direction === 'down' && scrollAccum > HIDE_THRESHOLD) {
        setHeaderVisible(false);
      } else if (direction === 'up' && scrollAccum > SHOW_THRESHOLD) {
        setHeaderVisible(true);
        setIsSticky(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Utility nav links (text links with arrow icons; links with asButton render as Button)
  const utilityNavItems = utilityLinks.map((link) => (
    <li key={link.label} className="utility-nav__item">
      {link.asButton ? (
          <Button className="gtm-top-nav gtm-header" href={link.href} variant="purple" size="sm" icon="external-link">
            {link.label}
          </Button>
        ) : (
          <a href={link.href} className="utility-nav__link gtm-top-nav gtm-header">
            {link.label}
            <svg className="utility-nav__arrow" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" alt="Black right arrow">
              <path d="M1 5h7M5.5 2.5 8 5 5.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        )}
    </li>
  ));

  // CTA button in utility row
  const ctaButton = ctaLink ? (
    <li className="utility-nav__item utility-nav__item--cta">
      <a href={ctaLink.href} className="utility-nav__cta gtm-top-nav gtm-header">
        {ctaLink.label}
        {ctaLink.external && (
          <svg className="utility-nav__cta-icon" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M8.5 5.5v3a.5.5 0 0 1-.5.5H1.5a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5H5M7 1h2v2M4.5 5.5 9 1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </a>
    </li>
  ) : null;

  const utilityNav = (
    <ul className="utility-nav">
      {utilityNavItems}
      {ctaButton}
    </ul>
  );

  // Main nav links with dropdown support
  const mainNavItems = mainNavLinks.map((link) => {
    const isActive = activeLink === link.label ||
      (link.href === '/' ? activeLink === '/' : activeLink.startsWith(link.href));
    const hasDropdown = link.children && link.children.length > 0;

    if (hasDropdown) {
      const childItems = link.children.map((child) => {
        const isChildActive = activeLink === child.href || activeLink === child.label;
        return (
          <li key={child.label} className={`main-nav__dropdown-item${isChildActive ? ' main-nav__dropdown-item--active' : ''}`}>
            <a href={child.href} className="gtm-sub-nav main-nav__dropdown-link">{renderLabel(child.label)}</a>
          </li>
        );
      });

      return (
        <li
          key={link.label}
          className={`main-nav__item main-nav__item--dropdown${isActive ? ' main-nav__item--active' : ''}${desktopDropdown === link.label ? ' main-nav__item--open' : ''}`}
          onBlur={(e) => {
            // Close the dropdown when focus moves outside this nav item.
            if (desktopDropdown === link.label && !e.currentTarget.contains(e.relatedTarget)) {
              setDesktopDropdown(null);
            }
          }}
        >
          <button
            className="gtm-top-nav gtm-header main-nav__dropdown-trigger"
            aria-haspopup="true"
            aria-expanded={desktopDropdown === link.label}
            type="button"
            onClick={() => toggleDesktopDropdown(link.label)}
          >
            {renderLabel(link.label)}
            <svg className="main-nav__chevron" width="8" height="5" viewBox="0 0 8 5" fill="none" aria-hidden="true" alt="Black arrow down">
              <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <ul className="main-nav__dropdown-list">{childItems}</ul>
        </li>
      );
    }

    return (
      <li key={link.label} className={`main-nav__item${isActive ? ' main-nav__item--active' : ''}`}>
        <a href={link.href} className="main-nav__link gtm-top-nav gtm-header">{renderLabel(link.label)}</a>
      </li>
    );
  });

  const mainNav = (
    <nav className="main-nav" aria-label="Main navigation">
      <ul className="main-nav__list">{mainNavItems}</ul>
    </nav>
  );

  // Extract CTA button from utility links (the asButton one)
  const utilityCtaLink = utilityLinks.find((link) => link.asButton);
  const otherUtilityLinks = utilityLinks.filter((link) => !link.asButton);

  return (
    <div className="header-wrapper" style={{ '--mobile-header-height': `${headerHeight}px` }}>
      <header ref={headerRef} className={`header ${!headerVisible ? ' header--hidden' : ''}${isSticky ? ' header--sticky' : ''}`}>
        {/* CTA Button at very top of mobile menu */}
        {utilityCtaLink && (
          <div className="mobile-nav__cta-top">
            <Button className="gtm-top-nav gtm-header" href={utilityCtaLink.href} variant="purple" size="md" icon="external-link">
              {utilityCtaLink.label}
            </Button>
          </div>
        )}
        <div className="header__inner container">
          <div className="logo">
            <a href={logoHref} className="logo__link gtm-top-nav gtm-header" aria-label={logoAlt}>
              <img src={logoSrc} alt={logoAlt} className="logo__image" />
            </a>
          </div>
          <div className="nav-area">
            {utilityNav}
            {mainNav}
          </div>
          <button
            className="mobile-menu-toggle gtm-top-nav gtm-header"
            type="button"
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span className="mobile-menu-toggle__label">
              {mobileMenuOpen ? 'Close' : 'Menu'}
            </span>
            <span className={`mobile-menu-toggle__icon ${mobileMenuOpen ? 'mobile-menu-toggle__icon--open' : ''}`}>
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
      </header>

      {/* Mobile Navigation Panel */}
      <nav
        className={`mobile-nav ${mobileMenuOpen ? 'mobile-nav--open' : ''}`}
        aria-label="Mobile navigation"
        aria-hidden={!mobileMenuOpen}
      >
        <ul className="mobile-nav__list">
          {mainNavLinks.map((link) => {
            const isActive = activeLink === link.label ||
              (link.href === '/' ? activeLink === '/' : activeLink.startsWith(link.href));
            const hasDropdown = link.children && link.children.length > 0;
            const isExpanded = expandedDropdown === link.label;
            const hasActiveChild = hasDropdown && link.children.some(
              (child) => activeLink === child.href || activeLink === child.label
            );

            return (
              <li key={link.label} className="mobile-nav__item">
                {hasDropdown ? (
                  <>
                    <button
                      className={`gtm-top-nav gtm-header mobile-nav__link mobile-nav__link--parent ${isActive || hasActiveChild ? 'mobile-nav__link--active' : ''} ${isExpanded ? 'mobile-nav__link--open' : ''}`}
                      type="button"
                      aria-expanded={isExpanded}
                      onClick={() => toggleDropdown(link.label)}
                    >
                      <span>
                        {renderLabel(link.label)}
                        <svg
                          className={`mobile-nav__chevron ${isExpanded ? 'mobile-nav__chevron--open' : ''}`}
                          width="8"
                          height="5"
                          viewBox="0 0 8 5"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </button>
                    {isExpanded && (
                      <ul className="mobile-nav__submenu">
                        {link.children.map((child) => {
                          const isChildActive = activeLink === child.href || activeLink === child.label;
                          return (
                            <li key={child.label} className={`mobile-nav__submenu-item${isChildActive ? ' mobile-nav__submenu-item--active' : ''}`}>
                              <a href={child.href} className="gtm-sub-nav mobile-nav__submenu-link">
                                {renderLabel(child.label)}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : (
                  <a
                    href={link.href}
                    className={`gtm-top-nav gtm-header mobile-nav__link ${isActive ? 'mobile-nav__link--active' : ''}`}
                  >
                    <span>{renderLabel(link.label)}</span>
                  </a>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mobile-nav__divider" />

        <ul className="mobile-nav__utility">
          {otherUtilityLinks.map((link) => (
            <li key={link.label} className="mobile-nav__utility-item">
              <a href={link.href} className="gtm-top-nav gtm-header mobile-nav__utility-link">
                {link.label}
                <svg className="mobile-nav__utility-arrow" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path d="M1 5h7M5.5 2.5 8 5 5.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

Header.propTypes = {
  logoSrc: PropTypes.string,
  logoAlt: PropTypes.string,
  logoHref: PropTypes.string,
  activeLink: PropTypes.string,
  utilityLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      asButton: PropTypes.bool,
    })
  ),
  ctaLink: PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
    external: PropTypes.bool,
  }),
  mainNavLinks: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        href: PropTypes.string.isRequired,
      }),
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        children: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            href: PropTypes.string.isRequired,
          })
        ),
      }),
    ])
  ),
};
